import React, { useEffect, useState } from 'react';
import { hrmsListJobs, hrmsCreateJob, hrmsUpdateJob, hrmsDeleteJob } from '../../lib/hrmsApi';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogTitle } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { useToast } from '../../hooks/use-toast';
import { Plus, Pencil, Trash2, Eye, EyeOff, Loader2 } from 'lucide-react';

const empty = () => ({ title: '', department: '', location: '', employment_type: 'Full-time', work_mode: 'On-site', experience: '', qualification: '', skills: [], salary_range: '', description: '', responsibilities: '', requirements: '', openings: 1, status: 'draft' });

const JobsPage = () => {
  const { toast } = useToast();
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => setRows(await hrmsListJobs().catch(() => []));
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const body = { ...editing, openings: Number(editing.openings) || 1, skills: typeof editing.skills === 'string' ? editing.skills.split(',').map(s => s.trim()).filter(Boolean) : editing.skills };
      if (editing.id) await hrmsUpdateJob(editing.id, body);
      else await hrmsCreateJob(body);
      toast({ title: 'Saved' });
      setEditing(null); load();
    } catch (err) {
      toast({ title: 'Save failed', description: err?.response?.data?.detail || '' });
    } finally { setSaving(false); }
  };

  const toggleStatus = async (j) => {
    const next = j.status === 'published' ? 'closed' : 'published';
    await hrmsUpdateJob(j.id, { status: next });
    load();
  };

  const remove = async (j) => {
    if (!window.confirm(`Archive job ${j.job_id}?`)) return;
    await hrmsDeleteJob(j.id);
    load();
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <div><h1 className="text-2xl font-extrabold">Jobs</h1><p className="text-sm text-slate-500">Publish jobs to the public Careers page.</p></div>
        <Button onClick={() => setEditing(empty())} className="bg-orange-500 hover:bg-orange-600"><Plus className="h-4 w-4 mr-1" /> New job</Button>
      </div>
      <div className="mt-6 rounded-2xl border bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-100 text-slate-700"><tr>
            <th className="text-left px-4 py-3">Job ID</th><th className="text-left px-4 py-3">Title</th><th className="text-left px-4 py-3">Dept</th><th className="text-left px-4 py-3">Location</th><th className="text-left px-4 py-3">Openings</th><th className="text-left px-4 py-3">Status</th><th className="text-left px-4 py-3">Actions</th>
          </tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-500">No jobs yet. Click <b>New job</b> to create one.</td></tr>}
            {rows.map((j) => (
              <tr key={j.id} className="border-t">
                <td className="px-4 py-3 font-mono text-xs">{j.job_id}</td>
                <td className="px-4 py-3 font-medium text-slate-900">{j.title}</td>
                <td className="px-4 py-3">{j.department || '—'}</td>
                <td className="px-4 py-3">{j.location || '—'}</td>
                <td className="px-4 py-3">{j.openings}</td>
                <td className="px-4 py-3"><Badge className={j.status === 'published' ? 'bg-emerald-500' : j.status === 'closed' ? 'bg-slate-500' : 'bg-amber-500'}>{j.status}</Badge></td>
                <td className="px-4 py-3 space-x-1">
                  <Button variant="outline" onClick={() => setEditing({ ...j, skills: (j.skills || []).join(', ') })} className="h-8 px-2"><Pencil className="h-3.5 w-3.5" /></Button>
                  <Button variant="outline" onClick={() => toggleStatus(j)} className="h-8 px-2">{j.status === 'published' ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}</Button>
                  <Button variant="outline" onClick={() => remove(j)} className="h-8 px-2 text-red-600 border-red-200"><Trash2 className="h-3.5 w-3.5" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!editing} onOpenChange={(v) => { if (!v) setEditing(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogTitle>{editing?.id ? 'Edit job' : 'New job'}</DialogTitle>
          {editing && (
            <div className="grid grid-cols-2 gap-3 mt-2">
              <div className="col-span-2"><Label>Job title *</Label><Input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} /></div>
              <div><Label>Department</Label><Input value={editing.department} onChange={(e) => setEditing({ ...editing, department: e.target.value })} /></div>
              <div><Label>Location</Label><Input value={editing.location} onChange={(e) => setEditing({ ...editing, location: e.target.value })} /></div>
              <div><Label>Employment type</Label><Input value={editing.employment_type} onChange={(e) => setEditing({ ...editing, employment_type: e.target.value })} /></div>
              <div><Label>Work mode</Label><Input value={editing.work_mode} onChange={(e) => setEditing({ ...editing, work_mode: e.target.value })} placeholder="Remote / Hybrid / On-site" /></div>
              <div><Label>Experience</Label><Input value={editing.experience} onChange={(e) => setEditing({ ...editing, experience: e.target.value })} placeholder="3-5 years" /></div>
              <div><Label>Qualification</Label><Input value={editing.qualification} onChange={(e) => setEditing({ ...editing, qualification: e.target.value })} /></div>
              <div className="col-span-2"><Label>Skills (comma-separated)</Label><Input value={editing.skills} onChange={(e) => setEditing({ ...editing, skills: e.target.value })} /></div>
              <div><Label>Salary range</Label><Input value={editing.salary_range} onChange={(e) => setEditing({ ...editing, salary_range: e.target.value })} placeholder="$60k-$90k" /></div>
              <div><Label>Openings</Label><Input type="number" value={editing.openings} onChange={(e) => setEditing({ ...editing, openings: e.target.value })} /></div>
              <div className="col-span-2"><Label>Description</Label><Textarea rows={4} value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} /></div>
              <div className="col-span-2"><Label>Responsibilities</Label><Textarea rows={3} value={editing.responsibilities} onChange={(e) => setEditing({ ...editing, responsibilities: e.target.value })} /></div>
              <div className="col-span-2"><Label>Requirements</Label><Textarea rows={3} value={editing.requirements} onChange={(e) => setEditing({ ...editing, requirements: e.target.value })} /></div>
              <div className="col-span-2">
                <Label>Status</Label>
                <select value={editing.status} onChange={(e) => setEditing({ ...editing, status: e.target.value })} className="mt-1 h-10 w-full rounded-md border border-slate-200 px-3 text-sm">
                  <option value="draft">Draft</option><option value="published">Published</option><option value="closed">Closed</option>
                </select>
              </div>
              <div className="col-span-2 flex justify-end gap-2 mt-2">
                <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
                <Button onClick={save} disabled={saving} className="bg-orange-500 hover:bg-orange-600">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default JobsPage;
